import { z } from 'zod';
import { dateRangeFields, paginationSchema } from './schemas';

/* ------------------------------------------------------------------ polling */

/** Access methods that can read interface counters. */
export const POLL_KINDS = ['snmp_v2c', 'snmp_v3', 'routeros_rest', 'routeros_api', 'fortios_rest', 'nxapi'] as const;
export type PollKind = (typeof POLL_KINDS)[number];

export const deviceMonitoringSchema = z.object({
  enabled: z.boolean(),
  credentialKind: z.enum(POLL_KINDS),
  /** Seconds between polls. */
  intervalSeconds: z.number().int().min(10).max(3600).default(60),
});
export type DeviceMonitoringInput = z.infer<typeof deviceMonitoringSchema>;

export const monitoringSettingsSchema = z.object({
  /** Raw (per-poll) samples are kept this many days. */
  rawDays: z.number().int().min(1).max(90).default(7),
  /** 5-minute aggregates. */
  fiveMinuteDays: z.number().int().min(7).max(730).default(90),
  /** Hourly aggregates. */
  hourlyDays: z.number().int().min(30).max(1825).default(730),
});

export const RATE_RANGES = ['1h', '6h', '24h', '7d', '30d'] as const;
export type RateRange = (typeof RATE_RANGES)[number];
/** Which resolution backs each range: raw samples, 5-minute or hourly aggregates. */
export const RANGE_RESOLUTION: Record<RateRange, { seconds: number; table: 'raw' | '5m' | '1h' }> = {
  '1h': { seconds: 3600, table: 'raw' },
  '6h': { seconds: 6 * 3600, table: 'raw' },
  '24h': { seconds: 24 * 3600, table: '5m' },
  '7d': { seconds: 7 * 86400, table: '5m' },
  '30d': { seconds: 30 * 86400, table: '1h' },
};

/** 'custom' uses from / to (whole days in the organization's time zone): 5-minute data up to 8 days, hourly beyond. */
export const rateHistoryQuerySchema = z.object({ range: z.enum([...RATE_RANGES, 'custom']).default('1h'), ...dateRangeFields });

export const portListQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(100).optional(),
  deviceId: z.string().uuid().optional(),
  /** Ports of this device and the switch/router ports cabled to it (where a server's traffic is measured). */
  servingDeviceId: z.string().uuid().optional(),
  /** Ports of one customer's equipment and the ports cabled to it. */
  customerId: z.string().uuid().optional(),
  datacenterId: z.string().uuid().optional(),
  sort: z.enum(['utilization', 'traffic', 'name', 'errors']).default('traffic'),
  /** Only ports that count towards totals (uplinks/transit). */
  totalsOnly: z.enum(['true', 'false']).optional(),
});

/* ------------------------------------------------------------------ alerts */

export const ALERT_METRICS = ['util_max', 'util_in', 'util_out', 'in_bps', 'out_bps', 'errors_ps', 'discards_ps', 'oper_down', 'device_unreachable'] as const;
export type AlertMetric = (typeof ALERT_METRICS)[number];
export const ALERT_METRIC_LABELS: Record<AlertMetric, string> = {
  util_max: 'Utilization, either direction (%)',
  util_in: 'Inbound utilization (%)',
  util_out: 'Outbound utilization (%)',
  in_bps: 'Inbound traffic (bit/s)',
  out_bps: 'Outbound traffic (bit/s)',
  errors_ps: 'Interface errors (per second)',
  discards_ps: 'Discarded packets (per second)',
  oper_down: 'Port down (enabled but link down)',
  device_unreachable: 'Device not answering polls',
};
/** Metrics with no threshold (a state, not a number). */
export const STATE_METRICS: readonly AlertMetric[] = ['oper_down', 'device_unreachable'];

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_SCOPES = ['all', 'totals', 'datacenter', 'devices', 'interfaces'] as const;
export const ALERT_SCOPE_LABELS: Record<(typeof ALERT_SCOPES)[number], string> = {
  all: 'Every monitored port',
  totals: 'Ports that count towards totals (uplinks, transit)',
  datacenter: 'Ports in one datacenter',
  devices: 'Ports on selected devices',
  interfaces: 'Selected ports',
};

const uuid = z.string().uuid();

export const alertRuleSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean().default(true),
    metric: z.enum(ALERT_METRICS),
    comparator: z.enum(['gt', 'lt']).default('gt'),
    threshold: z.number().min(0).max(1e13).default(0),
    /** The condition must hold this long… */
    forSeconds: z.number().int().min(0).max(86400).default(300),
    /** …and for at least this many consecutive samples. */
    minSamples: z.number().int().min(1).max(60).default(3),
    /** Consecutive good samples needed to resolve (avoids flapping). */
    clearSamples: z.number().int().min(1).max(60).default(2),
    severity: z.enum(ALERT_SEVERITIES).default('warning'),
    scope: z.enum(ALERT_SCOPES).default('all'),
    datacenterId: uuid.nullable().optional(),
    deviceIds: z.array(uuid).max(500).default([]),
    interfaceIds: z.array(uuid).max(2000).default([]),
    channelIds: z.array(uuid).max(20).default([]),
    notifyOnResolve: z.boolean().default(true),
  })
  .superRefine((r, ctx) => {
    if (r.scope === 'datacenter' && !r.datacenterId) ctx.addIssue({ code: 'custom', path: ['datacenterId'], message: 'Choose a datacenter' });
    if (r.scope === 'devices' && !r.deviceIds.length) ctx.addIssue({ code: 'custom', path: ['deviceIds'], message: 'Choose at least one device' });
    if (r.scope === 'interfaces' && !r.interfaceIds.length) ctx.addIssue({ code: 'custom', path: ['interfaceIds'], message: 'Choose at least one port' });
    if (r.metric.startsWith('util_') && r.threshold > 100) ctx.addIssue({ code: 'custom', path: ['threshold'], message: 'Utilization is a percentage (0–100)' });
    if (r.metric === 'device_unreachable' && (r.scope === 'interfaces' || r.scope === 'totals')) {
      ctx.addIssue({ code: 'custom', path: ['scope'], message: 'Device reachability applies to devices: use all, datacenter or devices' });
    }
  });
export type AlertRuleInput = z.infer<typeof alertRuleSchema>;

export const alertListQuerySchema = paginationSchema.extend({
  status: z.enum(['firing', 'resolved', 'all']).default('firing'),
  /** Alerts that started on these days. */
  ...dateRangeFields,
  severity: z.enum(ALERT_SEVERITIES).optional(),
  deviceId: uuid.optional(),
});
export const alertAckSchema = z.object({ note: z.string().trim().max(500).optional() });

export const maintenanceSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    scope: z.enum(['all', 'datacenter', 'devices']).default('devices'),
    datacenterId: uuid.nullable().optional(),
    deviceIds: z.array(uuid).max(500).default([]),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((m, ctx) => {
    if (new Date(m.endsAt) <= new Date(m.startsAt)) ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'Must end after it starts' });
    if (new Date(m.endsAt).getTime() - new Date(m.startsAt).getTime() > 31 * 86400_000) ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'At most 31 days' });
    if (m.scope === 'datacenter' && !m.datacenterId) ctx.addIssue({ code: 'custom', path: ['datacenterId'], message: 'Choose a datacenter' });
    if (m.scope === 'devices' && !m.deviceIds.length) ctx.addIssue({ code: 'custom', path: ['deviceIds'], message: 'Choose at least one device' });
  });

/* ------------------------------------------------------------------ notifications */

export const CHANNEL_KINDS = ['email', 'webhook', 'slack', 'telegram'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];
export const CHANNEL_KIND_LABELS: Record<ChannelKind, string> = { email: 'Email (SMTP)', webhook: 'Webhook (signed JSON)', slack: 'Slack', telegram: 'Telegram' };

const httpsUrl = z
  .string()
  .trim()
  .url()
  .max(500)
  .refine((u) => /^https?:\/\//i.test(u) && !/^https?:\/\/[^/]*@/i.test(u), 'Use an http(s) URL without credentials');
const secret = z.string().min(8).max(500);

export const channelSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('email'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean().default(true),
    to: z.array(z.string().trim().email()).min(1).max(20),
    from: z.string().trim().email(),
    smtpHost: z.string().trim().min(1).max(255),
    smtpPort: z.number().int().min(1).max(65535).default(587),
    /** 'tls' = implicit TLS (465), 'starttls' = upgrade (587), 'none' = lab only. */
    smtpSecurity: z.enum(['starttls', 'tls', 'none']).default('starttls'),
    smtpUser: z.string().trim().max(255).nullable().optional(),
    smtpPassword: z.string().max(500).nullable().optional(),
  }),
  z.object({
    kind: z.literal('webhook'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean().default(true),
    url: httpsUrl,
    /** HMAC-SHA256 signing secret (X-CDCIM-Signature). */
    signingSecret: secret,
  }),
  z.object({
    kind: z.literal('slack'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean().default(true),
    webhookUrl: z
      .string()
      .trim()
      .max(500)
      .regex(/^https:\/\/hooks\.slack\.com\/[A-Za-z0-9/_-]+$/, 'A Slack incoming-webhook URL (https://hooks.slack.com/…)'),
  }),
  z.object({
    kind: z.literal('telegram'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean().default(true),
    botToken: z.string().trim().regex(/^\d+:[A-Za-z0-9_-]{20,}$/, 'Looks like 123456:ABC-…'),
    chatId: z.string().trim().regex(/^-?\d+$|^@[A-Za-z0-9_]{5,}$/, 'A numeric chat id or @channel'),
  }),
]);
export type ChannelInput = z.infer<typeof channelSchema>;
/** Fields of each channel kind that are secret (encrypted, never returned). */
export const CHANNEL_SECRET_FIELDS: Record<ChannelKind, string[]> = {
  email: ['smtpPassword'],
  webhook: ['signingSecret'],
  slack: ['webhookUrl'],
  telegram: ['botToken'],
};

/** Interfaces to pin on the monitoring dashboard (shared by the organization's staff). */
export const dashboardAddSchema = z.object({ interfaceIds: z.array(z.string().uuid()).min(1).max(48) });
/** The dashboard's interfaces in their new order. */
export const dashboardOrderSchema = z.object({ interfaceIds: z.array(z.string().uuid()).max(48) });
export const DASHBOARD_MAX_ITEMS = 48;

/* ---------------------------------------------------- bandwidth per IP (flows) */

/** A router that sends flow records (NetFlow v5/v9, IPFIX — e.g. MikroTik Traffic Flow) to the collector. */
export const flowExporterSchema = z.object({
  name: z.string().trim().min(1).max(100),
  /** The address the router sends from (its source address towards this server). */
  address: z.string().trim().ip(),
  deviceId: z.string().uuid().nullable().optional(),
  /** 1-in-N packet sampling configured on the router when it does not announce it (MikroTik: 1). */
  samplingRate: z.number().int().min(1).max(1_000_000).default(1),
  enabled: z.boolean().default(true),
  /** Real-time per-IP rates every second (MikroTik Torch over the device's RouterOS API credential): the interface to watch, e.g. the uplink. */
  liveInterface: z.string().trim().min(1).max(64).regex(/^[^\r\n\0=]+$/, 'Not a valid interface name').nullable().optional(),
  liveDirection: z.enum(['uplink', 'downlink']).default('uplink'),
});
export type FlowExporterInput = z.infer<typeof flowExporterSchema>;

export const FLOW_RANGES = ['15m', '1h', '6h', '24h', '7d', '30d'] as const;
export type FlowRange = (typeof FLOW_RANGES)[number];
export const FLOW_RANGE_LABELS: Record<FlowRange, string> = { '15m': 'Last 15 minutes', '1h': 'Last hour', '6h': 'Last 6 hours', '24h': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' };

export const topIpQuerySchema = z.object({
  range: z.enum([...FLOW_RANGES, 'custom']).default('1h'),
  ...dateRangeFields,
  sort: z.enum(['download', 'upload', 'total']).default('download'),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  /** Part of an address, DNS name, device or customer name. */
  q: z.string().trim().max(100).optional(),
  /** Staff: one customer's addresses. */
  customerId: z.string().uuid().optional(),
});

export const ipHistoryQuerySchema = z.object({ range: z.enum([...FLOW_RANGES, 'custom']).default('24h'), ...dateRangeFields });

/** Remote addresses an address exchanged traffic with: live (last minute / second) or over a period (hourly data). */
export const ipPeersQuerySchema = z.object({ range: z.enum(['live', ...FLOW_RANGES, 'custom']).default('live'), ...dateRangeFields, limit: z.coerce.number().int().min(1).max(100).default(50) });

/** ASN and location of a remote address (from the offline DB-IP / GeoLite2 databases). */
export interface PeerGeoT {
  private?: boolean;
  asn: number | null;
  asName: string | null;
  country: string | null;
  countryName: string | null;
  region: string | null;
  city: string | null;
}

/** Live ranking: rates over the last minute of flow records. */
export const liveIpQuerySchema = z.object({
  sort: z.enum(['download', 'upload', 'total']).default('download'),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  q: z.string().trim().max(100).optional(),
  customerId: z.string().uuid().optional(),
});
